import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import { TYPOGRAPHY } from '../constants/typography';

gsap.registerPlugin(ScrollTrigger);

const ContactPage = () => {
    // Icon rendering helper
    const renderIcon = (name, size = 24) => {
        const icons = {
            phone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
            mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
            mapPin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
            clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
            globe: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
        };
        return icons[name] || null;
    };

    useEffect(() => {
        // Hero animations
        gsap.fromTo('.contact-hero-title', 
            {
                y: 50,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 1,
                ease: 'power3.out'
            }
        );

        gsap.fromTo('.contact-hero-subtitle', 
            {
                y: 30,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                delay: 0.2,
                ease: 'power3.out'
            }
        );

        // Tagline animation
        gsap.fromTo('.contact-tagline',
            {
                opacity: 0,
                scale: 0.95
            },
            {
                opacity: 1,
                scale: 1,
                duration: 0.8,
                delay: 0.5,
                ease: 'back.out(1.7)'
            }
        );

        // Contact stats animation
        gsap.fromTo('.contact-stat',
            {
                opacity: 0,
                y: 30
            },
            {
                opacity: 1,
                y: 0,
                duration: 0.6,
                stagger: 0.15,
                delay: 0.8,
                ease: 'power3.out'
            }
        );

        // Contact info animation with scale
        gsap.fromTo('.contact-info-item',
            {
                y: 40,
                opacity: 0,
                scale: 0.95
            },
            {
                y: 0,
                opacity: 1,
                scale: 1,
                scrollTrigger: {
                    trigger: '.contact-info-section',
                    start: 'top 80%',
                },
                duration: 0.6,
                stagger: 0.12,
                ease: 'power3.out'
            }
        );

        // Form animation
        gsap.fromTo('.contact-form',
            {
                x: 50,
                opacity: 0
            },
            {
                x: 0,
                opacity: 1,
                scrollTrigger: {
                    trigger: '.contact-form',
                    start: 'top 80%',
                },
                duration: 0.8,
                ease: 'power3.out'
            }
        );

        // CTA animation
        gsap.fromTo('.cta-content',
            {
                y: 40,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                scrollTrigger: {
                    trigger: '.cta-content',
                    start: 'top 85%',
                },
                duration: 0.8,
                ease: 'power3.out'
            }
        );

        // Subtle continuous pulse animation for tagline (optional)
        // gsap.to('.contact-tagline', {
        //     scale: 1.02,
        //     duration: 2,
        //     repeat: -1,
        //     yoyo: true,
        //     ease: 'sine.inOut'
        // });
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Here you would typically send the data to your backend
        console.log('Form submitted:', formData);
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
        }, 3000);
    };

    return (
        <div>
            <Helmet>
                <title>Contact Us - Hav'n Interiors</title>
                <meta name="description" content="Get in touch with Hav'n Interiors. Contact us for interior design consultations, project inquiries, and global sourcing solutions." />
                <meta name="keywords" content="contact interior designer, design consultation, Chennai interior design, luxury interiors contact" />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section className="hero" style={{ 
                height: '65vh',
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=90)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                paddingTop: '120px'
            }}>
                <div className="hero-content" style={{ position: 'relative', zIndex: 2, textShadow: '0 2px 10px rgba(0, 0, 0, 0.7)' }}>
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ 
                            fontSize: '14px',
                            color: 'var(--accent-color)',
                            letterSpacing: '3px',
                            textTransform: 'uppercase',
                            fontWeight: '600'
                        }}>
                           
                        </span>
                    </div>
                    <h1 className="contact-hero-title title-responsive" style={{ fontSize: '56px', fontWeight: '300', color: '#F5F1ED', marginBottom: '18px' }}>
                        Get in Touch
                    </h1>
                    <p className="contact-hero-subtitle hero-subtitle" style={{ 
                        fontSize: '18px', 
                        color: '#F5F1ED', 
                        fontWeight: '400', 
                        maxWidth: '800px', 
                        margin: '0 auto 18px', 
                        lineHeight: '1.6',
                        textShadow: '0 3px 15px rgba(0, 0, 0, 0.9), 0 1px 3px rgba(0, 0, 0, 1)',
                        opacity: '1'
                    }}>
                        Have a project in mind? Want to explore our design studios?<br />
                        We're here to help bring your vision to life.
                    </p>
                    <div style={{ margin: '25px auto 30px' }}>
                        <p className="contact-tagline" style={{ 
                            fontSize: '16px', 
                            color: 'var(--accent-color)', 
                            fontStyle: 'italic',
                            fontWeight: '400',
                            letterSpacing: '0.5px',
                            textShadow: '0 2px 10px rgba(0, 0, 0, 0.8), 0 1px 3px rgba(0, 0, 0, 1)'
                        }}>
                            Your Vision, Our Expertise — Let's Create Together
                        </p>
                    </div>
                    <div className="responsive-flex" style={{ 
                        marginTop: '35px', 
                        display: 'flex', 
                        gap: '40px', 
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}>
                        <div className="contact-stat stat-card" style={{ 
                            textAlign: 'center',
                            transition: 'all 0.3s ease',
                            minWidth: '200px'
                        }}>
                            <div style={{ marginBottom: '10px', color: 'var(--accent-color)' }}>{renderIcon('phone', 40)}</div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', letterSpacing: '1px', marginBottom: '5px' }}>CALL US</div>
                            <div style={{ fontSize: '18px', fontWeight: '400', color: 'var(--accent-color)' }}><a href="tel:+919500721148" style={{ fontSize: '18px', fontWeight: '400', color: 'var(--accent-color)' }}>+91 95007 21148,</a> <a href="tel:+919962466829" style={{ fontSize: '18px', fontWeight: '400', color: 'var(--accent-color)' }}>+91 99624 66829</a></div>
                        </div>
                        <div className="stat-divider" style={{ width: '1px', height: '80px', background: 'rgba(201, 169, 110, 0.3)' }} />
                        <div className="contact-stat stat-card" style={{
                            textAlign: 'center',
                            transition: 'all 0.3s ease',
                            minWidth: '200px'
                        }}>
                            <div style={{ marginBottom: '10px', color: 'var(--accent-color)' }}>{renderIcon('mail', 40)}</div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', letterSpacing: '1px', marginBottom: '5px' }}>EMAIL US</div>
                            <div style={{ fontSize: '18px', fontWeight: '400', color: 'var(--accent-color)' }}><a href="mailto:havninteriorsdesk@gmail.com" style={{ fontSize: '18px', fontWeight: '400', color: 'var(--accent-color)' }}>havninteriorsdesk@gmail.com</a></div>
                        </div>
                        <div className="stat-divider" style={{ width: '1px', height: '80px', background: 'rgba(201, 169, 110, 0.3)' }} />
                        <div className="contact-stat stat-card" style={{
                            textAlign: 'center',
                            transition: 'all 0.3s ease',
                            minWidth: '200px'
                        }}>
                            <div style={{ marginBottom: '10px', color: 'var(--accent-color)' }}>{renderIcon('mapPin', 40)}</div>
                            <div style={{ fontSize: '14px', color: 'rgba(245, 241, 237, 0.7)', letterSpacing: '1px', marginBottom: '5px' }}>VISIT US</div>
                            <div style={{ fontSize: '18px', fontWeight: '400', color: 'var(--accent-color)' }}>Opp. Nehru Stadium, Periamet
Chennai</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact-form-section" style={{ background: '#0F0F0F', padding: '60px 5%' }}>
                <div className="responsive-grid grid-2" style={{ maxWidth: '1400px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px' }}>
                    
                    {/* Contact Information */}
                    <div className="contact-info-section">
                        <h2 style={{ fontSize: '42px', fontWeight: '300', marginBottom: '40px', color: 'var(--accent-color)' }}>
                            Contact Information
                        </h2>
                        
                        <div className="responsive-grid grid-2" style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '25px'
                        }}>
                        
                        <div className="contact-info-item" style={{ 
                            padding: '20px',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.08)';
                            e.currentTarget.style.transform = 'translateX(10px)';
                            e.currentTarget.style.border = '1px solid rgba(201, 169, 110, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.border = '1px solid transparent';
                        }}>
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('phone', 24)} General Enquiries
                            </h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                <a href="tel:+919500721148" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 95007 21148
                                </a><br />
                                <a href="tel:+919962466829" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                     +91  9962466829
                                </a>
                            </p><br /><br />
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('phone', 24)} Procurement & Supply
                            </h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                <a href="tel:+919500721148" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 95007 21148
                                </a>
                            </p><br /><br />
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('phone', 24)} Corporate & Legal Affairs
                            </h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                <a href="tel:+917290085871" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +917290085871
                                </a><br />
                                <a href="tel:+919884178180" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 98841 78180
                                </a>								
                            </p>														
                        </div>

                        <div className="contact-info-item" style={{ 
                            padding: '20px',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.08)';
                            e.currentTarget.style.transform = 'translateX(10px)';
                            e.currentTarget.style.border = '1px solid rgba(201, 169, 110, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.border = '1px solid transparent';
                        }}>
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('phone', 24)} Sales & Distributorship
                            </h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                <a href="tel:+919566277093" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 95662 77093
                                </a>
                            </p><br /><br />
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('phone', 24)} Accounts Department
                            </h3>							
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                <a href="tel:+919500721137" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 95007 21137
                                </a><br />
                                <a href="tel:+919500721165" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 95007 21165
                                </a><br />		
                                <a href="tel:+919500721152" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 95007 21152
                                </a>								
                            </p><br /><br />			
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('phone', 24)} Admin & HR
                            </h3>							
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                <a href="tel:+917825821280" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 78258 21280
                                </a><br />
                                <a href="tel:+917825821279" style={{ color: 'rgba(245, 241, 237, 0.8)', textDecoration: 'none', transition: 'color 0.3s' }}>
                                    +91 78258 21279
                                </a><br />								
                            </p>														
                        </div>

                        <div className="contact-info-item" style={{ 
                            padding: '20px',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.08)';
                            e.currentTarget.style.transform = 'translateX(10px)';
                            e.currentTarget.style.border = '1px solid rgba(201, 169, 110, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.border = '1px solid transparent';
                        }}>
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('mapPin', 24)} Corporate Headquarters
                            </h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                 9/2 (5/1-B), Letang’s Road, <br />
                                Purasawalkam, Chennai – 600007,<br />
								Tamil Nadu, India<br />
                                <a href="https://maps.app.goo.gl/f2AmKQ5Dy7ENUGgz7" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)', textDecoration: 'none', marginTop: '8px', display: 'inline-block', transition: 'transform 0.3s' }}>
                                    View on Map →
                                </a>
                            </p>
                        </div>

                        <div className="contact-info-item" style={{ 
                            padding: '20px',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.08)';
                            e.currentTarget.style.transform = 'translateX(10px)';
                            e.currentTarget.style.border = '1px solid rgba(201, 169, 110, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.border = '1px solid transparent';
                        }}>
                            <h3 style={{ fontSize: '16px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('mapPin', 24)} Hav’n Design Experience Centre
                            </h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.8)', paddingLeft: '34px' }}>
                                No. 88/1, Raja Muthiah Road<br />
                                Opp. Nehru Stadium, Periamet <br />
								Chennai – 600007, Tamil Nadu, India<br />
                                <a href="mailto:havninteriorsdesk@gmail.com" style={{ color: 'var(--accent-color)', textDecoration: 'none', marginTop: '8px', display: 'inline-block', transition: 'transform 0.3s' }}> havninteriorsdesk@gmail.com</a> <br />
								<a href="tel:04435009714" style={{ color: 'var(--accent-color)', textDecoration: 'none', marginTop: '8px', display: 'inline-block', transition: 'transform 0.3s' }}>044-35009714 </a> (30 Lines)<br /> <br />
								<a href="https://maps.app.goo.gl/5eDP93AJTpTdsts8A" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)', textDecoration: 'none', marginTop: '8px', display: 'inline-block', transition: 'transform 0.3s' }}>
                                    View on Map →
                                </a> 
                            </p>
                        </div>

                        {/* Social Media Links */}
                        <div className="contact-info-item" style={{ 
                            padding: '20px',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(201, 169, 110, 0.08)';
                            e.currentTarget.style.transform = 'translateX(10px)';
                            e.currentTarget.style.border = '1px solid rgba(201, 169, 110, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.border = '1px solid transparent';
                        }}>
                            <h3 style={{ fontSize: '20px', color: 'var(--accent-color)', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {renderIcon('globe', 24)} Connect With Us
                            </h3>
                            <div style={{ display: 'flex', gap: '15px', paddingLeft: '34px', flexWrap: 'wrap' }}>
                                <a href="https://wa.me/919791127791" target="_blank" rel="noopener noreferrer" 
                                   style={{ color: 'var(--accent-color)', fontSize: '16px', padding: '8px 16px', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '8px', textDecoration: 'none', transition: 'all 0.3s' }}
                                   onMouseEnter={(e) => { e.target.style.background = 'rgba(201, 169, 110, 0.1)'; e.target.style.transform = 'translateY(-2px)'; }}
                                   onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.transform = 'translateY(0)'; }}>
                                    WhatsApp
                                </a>
                                <a href="https://instagram.com/havninterior" target="_blank" rel="noopener noreferrer" 
                                   style={{ color: 'var(--accent-color)', fontSize: '16px', padding: '8px 16px', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '8px', textDecoration: 'none', transition: 'all 0.3s' }}
                                   onMouseEnter={(e) => { e.target.style.background = 'rgba(201, 169, 110, 0.1)'; e.target.style.transform = 'translateY(-2px)'; }}
                                   onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.transform = 'translateY(0)'; }}>
                                    Instagram
                                </a>
                                <a href="https://www.linkedin.com/company/havninterior" target="_blank" rel="noopener noreferrer" 
                                   style={{ color: 'var(--accent-color)', fontSize: '16px', padding: '8px 16px', border: '1px solid rgba(201, 169, 110, 0.3)', borderRadius: '8px', textDecoration: 'none', transition: 'all 0.3s' }}
                                   onMouseEnter={(e) => { e.target.style.background = 'rgba(201, 169, 110, 0.1)'; e.target.style.transform = 'translateY(-2px)'; }}
                                   onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.transform = 'translateY(0)'; }}>
                                    LinkedIn
                                </a>
                            </div>
                        </div>
                        
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="contact-form" style={{ 
                        background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.08) 0%, rgba(0, 0, 0, 0.3) 100%)', 
                        padding: 'clamp(20px, 5%, 50px)', 
                        borderRadius: '20px', 
                        border: '2px solid rgba(201, 169, 110, 0.3)',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h2 style={{ fontSize: '42px', fontWeight: '300', marginBottom: '15px', color: 'var(--accent-color)' }}>
                            Send Us a Message
                        </h2>
                        <p style={{ fontSize: '16px', color: 'rgba(245, 241, 237, 0.7)', marginBottom: '35px' }}>
                            Fill out the form below and we'll get back to you within 24 hours.
                        </p>

                        {submitted && (
                            <div style={{
                                padding: '16px 20px',
                                background: 'rgba(40, 167, 69, 0.15)',
                                border: '2px solid rgba(40, 167, 69, 0.4)',
                                borderRadius: '12px',
                                color: '#4ade80',
                                marginBottom: '25px',
                                fontSize: '15px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                animation: 'slideDown 0.3s ease'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' }}>
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Thank you! Your message has been sent successfully.
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="John Doe"
                                />
                            </div>

                            <div className="form-group">
                                <label>Email Address *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="john@example.com"
                                />
                            </div>

                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+91 98765 43210"
                                />
                            </div>

                            <div className="form-group">
                                <label>Subject *</label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    required
                                    placeholder="Project Inquiry"
                                />
                            </div>

                            <div className="form-group">
                                <label>Message *</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows="6"
                                    placeholder="Tell us about your project..."
                                />
                            </div>

                            <button 
                                type="submit" 
                                style={{ 
                                    width: '100%',
                                    padding: '16px',
                                    background: 'linear-gradient(135deg, #C9A96E 0%, #B8935A 100%)',
                                    color: '#000',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    border: 'none',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 15px rgba(201, 169, 110, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 8px 25px rgba(201, 169, 110, 0.5)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 15px rgba(201, 169, 110, 0.3)';
                                }}
                            >
                                Send Message →
                            </button>
                        </form>
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section style={{ padding: '0', background: '#0F0F0F' }}>
                <div style={{ 
                    width: '100%', 
                    padding: '60px 5%',
                    borderTop: '1px solid rgba(201, 169, 110, 0.2)'
                }}>
                    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <h2 style={{ fontSize: '42px', fontWeight: '300', marginBottom: '15px', color: 'var(--accent-color)' }}>
                                Visit Our Design Experience Centre
                            </h2>
                            <p style={{ fontSize: '16px', color: 'rgba(245, 241, 237, 0.7)', lineHeight: '1.8' }}>
                                Experience our 8 specialized design studios showcasing premium materials, finishes, and furniture from around the world
                            </p>
                        </div>
                        <div style={{ 
                            width: '100%', 
                            height: '450px',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            border: '2px solid rgba(201, 169, 110, 0.3)',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
                        }}>
                            <iframe 
                                src="https://www.google.com/maps/embed?q=9%2F2(5%2F1-B)+Lettangs+Road+Next+to+Adity+Audio+vision+Purasaiwakkam+CHENNAI+600007&output=embed" 
                                width="100%" 
                                height="100%" 
                                style={{ border: 0, filter: 'grayscale(20%) invert(90%) contrast(90%)' }}
                                allowFullScreen="" 
                                loading="lazy" 
                                referrerPolicy="no-referrer-when-downgrade"
                                title="HAV'N Interiors Location - Purasaiwakkam, Chennai"
                            />
                        </div>
                        <div style={{ 
                            marginTop: '30px', 
                            textAlign: 'center',
                            padding: '25px',
                            background: 'rgba(201, 169, 110, 0.05)',
                            borderRadius: '12px',
                            border: '1px solid rgba(201, 169, 110, 0.2)'
                        }}>
                            <p style={{ fontSize: '15px', color: 'rgba(245, 241, 237, 0.8)', lineHeight: '1.6', display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    {renderIcon('mapPin', 18)} <strong style={{ color: 'var(--accent-color)' }}>Chennai Office:</strong> 9/2(5/1-B), Lettangs Road, Next to Adity Audio Vision, Purasaiwakkam, Chennai - 600007
                                </span>
                                <a href="https://maps.app.goo.gl/aSsXWA2SQP5jH2R17" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)', textDecoration: 'none', marginLeft: '10px' }}>
                                    View on Google Maps →
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-content" style={{ 
                textAlign: 'center', 
                background: 'linear-gradient(135deg, #C9A96E 0%, #B8935A 100%)', 
                padding: '60px 5%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ maxWidth: '800px' }}>
                    <h2 className="title-responsive" style={{ fontSize: '48px', fontWeight: '300', marginTop: '0', marginBottom: '20px', color: '#000' }}>
                        Ready to Transform Your Space?
                    </h2>
                    <p style={{ fontSize: '18px', color: 'rgba(0, 0, 0, 0.8)', marginBottom: '35px', lineHeight: '1.6' }}>
                        Schedule a free consultation with our design experts and bring your vision to life.
                    </p>
                    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <a 
                            href="tel:+919791127791" 
                            style={{ 
                                padding: '16px 40px',
                                background: '#000',
                                color: '#C9A96E',
                                fontSize: '16px',
                                fontWeight: '600',
                                borderRadius: '50px',
                                textDecoration: 'none',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                                display: 'inline-block'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-3px)';
                                e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.3)';
                            }}
                        >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                {renderIcon('phone', 20)} Call Now
                            </span>
                        </a>
                        <a 
                            href="#contact-form-section" 
                            style={{ 
                                padding: '16px 40px',
                                background: 'transparent',
                                color: '#000',
                                fontSize: '16px',
                                fontWeight: '600',
                                border: '2px solid #000',
                                borderRadius: '50px',
                                textDecoration: 'none',
                                transition: 'all 0.3s ease',
                                display: 'inline-block'
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById('contact-form-section')?.scrollIntoView({ 
                                    behavior: 'smooth',
                                    block: 'start'
                                });
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#000';
                                e.target.style.color = '#C9A96E';
                                e.target.style.transform = 'translateY(-3px)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                                e.target.style.color = '#000';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                {renderIcon('mail', 20)} Email Us
                            </span>
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" style={{ width: '180px', marginBottom: '20px', opacity: 0.8 }} />
                <p>&copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.</p>
                <p style={{ marginTop: '10px', fontSize: '12px', opacity: 0.6 }}>Innovate. Integrate. Execute.</p>
            </footer>
        </div>
    );
};



export default ContactPage;
